import { useEffect, useMemo, useState } from "react";
import MainLayout from "../common/components/MainLayout";
import {
    Select,
    Radio,
    Button,
    Statistic,
    Table,
    Tag,
    Modal,
    message,
    Space,
    Tabs,
    Typography,
    Empty,
    DatePicker,
    Checkbox,
    InputNumber,
    Collapse,
} from "antd";
import { MailOutlined, SendOutlined } from "@ant-design/icons";
import moment, { Moment } from "moment";
import debounce from "lodash.debounce";
import locale from "antd/lib/date-picker/locale/es_ES";
import useUtils from "../common/hooks/useUtils";
import TagInput, { Tag as CodigoPostalTag } from "../common/components/TagInput";
import CollapsibleCard from "../common/components/CollapsibleCard";
import useLoteNotificacionAPI, {
    LoteImpresionOption,
    LoteImpresionVencidoOption,
    ConfiguracionProduccionOption,
    PreviewActa,
    TipoLoteNotificacion,
} from "./hooks/useLoteNotificacionAPI";
import EstadoLoteNotificacion, { ResumenEstado } from "./components/EstadoLoteNotificacion";
import LoteNotificacionDetalleModal from "./components/LoteNotificacionDetalleModal";
import HistorialLotesNotificacion from "./components/HistorialLotesNotificacion";
import ResumenLotesNotificacion from "./components/ResumenLotesNotificacion";

const { Text, Paragraph } = Typography;

type Alcance = "ambas" | "generadas" | "excluidas";

const ALCANCE_CAMPANIA_LABELS: Record<Alcance, string> = {
    generadas: "solo impresión generada",
    excluidas: "solo impresión excluida",
    ambas: "impresión generada y excluida",
};

// Un mismo municipio (Subtitulo) puede tener varias configuraciones de producción, una por fuente de
// infracciones (Radar, Semáforo, Luces Bajas...). El Nombre es lo que las distingue en el combo.
const nombreConfiguracionProduccion = (c: ConfiguracionProduccionOption): string =>
    c.Subtitulo ? `${c.Subtitulo} (${c.Nombre})` : c.Nombre;

// Producción y Producción vencida trabajan sobre un lote de impresión puntual; Campaña, sobre un municipio.
const esTipoPorLote = (tipo: TipoLoteNotificacion) => tipo === "PRODUCCION" || tipo === "PRODUCCION_VENCIDA";

// Se guarda el lote vencido elegido con la misma forma que los de Producción, para reusar el resto de la pantalla.
const aLoteImpresionOption = (l: LoteImpresionVencidoOption): LoteImpresionOption => ({
    Id: l.loteImpresionId,
    FechaHora: l.fechaHora,
    NroLote: l.nroLote,
    CantidadInfracciones: l.cantidadInfracciones,
    CantidadInfraccionesExcluidas: l.cantidadInfraccionesExcluidas,
    UnidadesProdNombre: l.unidadesProdNombre,
});

// Solo se aclara cuando alguna de las vencidas es de impresión excluida (no entra con "Solo generadas").
const textoExcluidasVencidas = (l: LoteImpresionVencidoOption): string => {
    const excluidas = l.cantidadVencidas - l.cantidadVencidasGeneradas;
    if (excluidas <= 0) return "";
    return excluidas === 1 ? " (1 excluida)" : ` (${excluidas} excluidas)`;
};

const DESCRIPCION_TIPO: Record<"PRODUCCION" | "CAMPANIA" | "PRODUCCION_VENCIDA", string> = {
    PRODUCCION: "Primer envío de actas recién impresas de un lote de impresión puntual.",
    PRODUCCION_VENCIDA: "Re-notificación de las actas vencidas (2º vencimiento pasado) de un lote de impresión puntual. Regenera cada acta con valores actualizados y un QR de pago nuevo antes de mandarla (más lento que producción). Siempre deja afuera las actas en convenio de pago.",
    CAMPANIA: "Re-notificación de actas impagas ya vencidas, sin importar si ya se notificaron antes. Regenera acta y QR de pago antes de mandar cada una (más lento que producción).",
};

export default () => {
    const api = useLoteNotificacionAPI();
    const { getProvinciasList } = useUtils();

    const [tipo, setTipo] = useState<TipoLoteNotificacion>("PRODUCCION");

    // PRODUCCION
    const [lotesImpresion, setLotesImpresion] = useState<LoteImpresionOption[]>([]);
    const [loadingLotesImpresion, setLoadingLotesImpresion] = useState(false);
    const [loteImpresionId, setLoteImpresionId] = useState<number>();
    // Guardado aparte de `lotesImpresion` a propósito: una vez elegido el lote, el usuario puede
    // seguir tipeando en el buscador y disparar otra búsqueda que reemplace la lista de opciones
    // (ver buscarLotesImpresion) sin perder de vista cuál quedó realmente seleccionado.
    const [loteImpresionSeleccionado, setLoteImpresionSeleccionado] = useState<LoteImpresionOption>();
    const [alcance, setAlcance] = useState<Alcance>("generadas");
    const [provinciasExcluir, setProvinciasExcluir] = useState<string[]>([]);
    const [cpExcluir, setCpExcluir] = useState<CodigoPostalTag[]>([]);

    // PRODUCCION_VENCIDA: mismo lote/alcance/exclusiones que Producción, pero el combo solo lista lotes
    // con actas vencidas.
    const [lotesVencidos, setLotesVencidos] = useState<LoteImpresionVencidoOption[]>([]);
    const [loadingLotesVencidos, setLoadingLotesVencidos] = useState(false);

    // CAMPANIA
    const [configuracionesProduccion, setConfiguracionesProduccion] = useState<ConfiguracionProduccionOption[]>([]);
    const [configuracionProduccionId, setConfiguracionProduccionId] = useState<number>();
    const [fechaInfraccionRange, setFechaInfraccionRange] = useState<[Moment, Moment] | null>(null);
    const [provinciasIncluirCampania, setProvinciasIncluirCampania] = useState<string[]>([]);
    const [provinciasExcluirCampania, setProvinciasExcluirCampania] = useState<string[]>([]);
    const [cpIncluirCampania, setCpIncluirCampania] = useState<CodigoPostalTag[]>([]);
    const [cpExcluirCampania, setCpExcluirCampania] = useState<CodigoPostalTag[]>([]);
    const [soloVencidasPreview, setSoloVencidasPreview] = useState(false);
    // "ambas" por defecto: la campaña incluye IMPRESION_GENERADA e IMPRESION_EXCLUIDA (comportamiento
    // que usa Fluxa). Neuralsys elige "generadas" para no re-notificar las actas excluidas de la impresión.
    // A diferencia de Producción, filtra por Estado (y no por ImpresionExcluida) porque es el mismo
    // campo con el que el backend decide si un acta es notificable.
    const [alcanceCampania, setAlcanceCampania] = useState<Alcance>("ambas");
    const [diasVencimiento, setDiasVencimiento] = useState<number>(0);
    const [mantenerValoresRegeneracion, setMantenerValoresRegeneracion] = useState(false);
    // Destildado por defecto: el código se comparte con otra empresa y no se le cambia el comportamiento.
    const [excluirConveniosCampania, setExcluirConveniosCampania] = useState(false);

    const [count, setCount] = useState<number>();
    const [sinEmail, setSinEmail] = useState<number>();
    const [enConvenio, setEnConvenio] = useState<number>();
    const [countLoading, setCountLoading] = useState(false);
    const [preview, setPreview] = useState<PreviewActa[]>([]);

    const [sending, setSending] = useState(false);
    const [loteEnCursoId, setLoteEnCursoId] = useState<number | null>(null);
    // El backend todavía no guarda de qué lote de impresión salió un envío (ver handoff), así que
    // esto solo sirve como referencia de "lo que acabo de mandar" en esta misma sesión del navegador
    // — no sobrevive a un refresh ni aparece más tarde en el historial.
    const [loteImpresionOrigenEnCurso, setLoteImpresionOrigenEnCurso] = useState<LoteImpresionOption | null>(null);
    const [estadoLoteEnCurso, setEstadoLoteEnCurso] = useState<ResumenEstado | null>(null);
    const [detalleLoteId, setDetalleLoteId] = useState<number | null>(null);
    const [historialKey, setHistorialKey] = useState(0);

    useEffect(() => {
        api.listarLotesImpresion().then((res) => setLotesImpresion(res.data?.value ?? []));
        api.listarConfiguracionesProduccion().then((res) => setConfiguracionesProduccion(res.data?.value ?? []));
    }, []);

    // Búsqueda server-side: el combo solo trae los 50 lotes de impresión más recientes, así que
    // el filtrado local (optionFilterProp) no alcanza para encontrar uno más viejo. Se busca por
    // número de lote exacto o por nombre de unidad de producción (ver buildLotesImpresionSearchFilter).
    const buscarLotesImpresion = useMemo(
        () => debounce((texto: string) => {
            setLoadingLotesImpresion(true);
            api.listarLotesImpresion(texto).then((res) => {
                setLotesImpresion(res.data?.value ?? []);
                setLoadingLotesImpresion(false);
            });
        }, 400),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const cargarLotesVencidos = (texto?: string) => {
        setLoadingLotesVencidos(true);
        api.listarLotesImpresionVencidos(texto).then((res) => {
            setLotesVencidos(res.data?.value ?? []);
            setLoadingLotesVencidos(false);
        });
    };

    // Cada búsqueda cuenta las actas vencidas de hasta 150 lotes en el backend: el debounce es más largo
    // que el del combo de Producción.
    const buscarLotesVencidos = useMemo(
        () => debounce((texto: string) => cargarLotesVencidos(texto), 600),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    useEffect(() => {
        if (tipo === "PRODUCCION_VENCIDA") cargarLotesVencidos();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tipo]);

    // En PRODUCCION / PRODUCCION_VENCIDA hace falta elegir un lote de impresión puntual; en CAMPANIA, un
    // municipio (configuración de producción) — ver sección 0/1bis del handoff.
    const formularioListo = tipo === "CAMPANIA" ? configuracionProduccionId != null : loteImpresionId != null;

    const buildFilter = (): string[] => {
        if (!formularioListo) return [];
        // PRODUCCION_VENCIDA usa el mismo filtro: la condición de vencida y la exclusión de convenios
        // las aplica siempre el backend según el tipo.
        if (esTipoPorLote(tipo)) {
            const filters = [`LoteImpresionId eq ${loteImpresionId}`];
            if (alcance === "generadas") filters.push("ImpresionExcluida eq false");
            if (alcance === "excluidas") filters.push("ImpresionExcluida eq true");
            provinciasExcluir.forEach((p) => filters.push(`Titular/ProvinciaTitular ne '${p}'`));
            cpExcluir.forEach((c) => filters.push(`Titular/CodigoPostalTitular ne '${c.Nombre}'`));
            return filters;
        }

        const filters = [`Equipo/ConfiguracionProduccionId eq ${configuracionProduccionId}`];
        if (alcanceCampania === "generadas") filters.push("Estado eq 'IMPRESION_GENERADA'");
        if (alcanceCampania === "excluidas") filters.push("Estado eq 'IMPRESION_EXCLUIDA'");
        if (fechaInfraccionRange) {
            filters.push(`FechaHora ge ${moment(fechaInfraccionRange[0]).startOf("day").toISOString()}`);
            filters.push(`FechaHora le ${moment(fechaInfraccionRange[1]).endOf("day").toISOString()}`);
        }
        if (provinciasIncluirCampania.length) filters.push(`Titular/ProvinciaTitular in (${provinciasIncluirCampania.map((p) => `'${p}'`).join(",")})`);
        if (provinciasExcluirCampania.length) filters.push(`not (Titular/ProvinciaTitular in (${provinciasExcluirCampania.map((p) => `'${p}'`).join(",")}))`);
        if (cpIncluirCampania.length) filters.push(`Titular/CodigoPostalTitular in (${cpIncluirCampania.map((c) => `'${c.Nombre}'`).join(",")})`);
        if (cpExcluirCampania.length) filters.push(`not (Titular/CodigoPostalTitular in (${cpExcluirCampania.map((c) => `'${c.Nombre}'`).join(",")}))`);
        return filters;
    };

    // La condición de "ya vencida" es solo una ayuda visual del preview (sección 1bis del
    // handoff) — a propósito no se manda en el envío real, para que reanudar un lote de
    // campaña más tarde no deje de encontrar sus propias actas (que dejan de estar vencidas
    // apenas se regeneran).
    const buildFilterPreview = (): string[] => {
        const base = buildFilter();
        if (tipo === "CAMPANIA" && soloVencidasPreview) {
            const hoy = moment().startOf("day").toISOString();
            return [...base, `FechaVencimiento lt ${hoy}`, `Fecha2doVencimiento lt ${hoy}`];
        }
        return base;
    };

    useEffect(() => {
        if (!formularioListo) {
            setCount(undefined);
            setSinEmail(undefined);
            setEnConvenio(undefined);
            setPreview([]);
            return;
        }
        setCountLoading(true);
        const timeout = setTimeout(async () => {
            const filter = buildFilterPreview();
            const res = await api.previsualizarEnvio(filter, 20, 0, {
                tipo,
                excluirConvenios: tipo === "CAMPANIA" && excluirConveniosCampania,
            });
            setCount(res.data?.value.cantidadConEmail);
            setSinEmail(res.data?.value.cantidadSinEmail);
            setEnConvenio(res.data?.value.cantidadEnConvenio);
            setPreview(res.data?.value.actas ?? []);
            setCountLoading(false);
        }, 800);
        return () => clearTimeout(timeout);
    }, [
        tipo, loteImpresionId, alcance, provinciasExcluir, cpExcluir,
        configuracionProduccionId, fechaInfraccionRange, provinciasIncluirCampania,
        provinciasExcluirCampania, cpIncluirCampania, cpExcluirCampania, soloVencidasPreview,
        alcanceCampania, excluirConveniosCampania,
    ]);


    // Mientras el propio lote esté sin terminar, no dejamos disparar otro: si los filtros se
    // pisan con actas ya pendientes en el lote en curso, el destinatario puede recibir el mail
    // duplicado (el backend ya excluye automáticamente las que están en OTRO lote en curso, pero
    // no evita que este mismo front dispare dos envíos propios superpuestos — sección 6).
    const hayLotePropioActivo = estadoLoteEnCurso != null && !estadoLoteEnCurso.completado;

    const enviarLote = async () => {
        setSending(true);
        const result = await api.enviarLoteEmail(
            buildFilter(),
            tipo,
            tipo === "CAMPANIA"
                ? { diasVencimiento, mantenerValoresRegeneracion, excluirConvenios: excluirConveniosCampania }
                : { loteImpresionId }
        );
        setSending(false);
        if (result.error) {
            message.error("Error al iniciar el envío");
            return;
        }
        const { cantidadActasTotal, lotes, actasExcluidasPorEnCurso } = result.data!.value;
        if (lotes.length === 0) {
            message.warning("No hay actas que coincidan con el filtro seleccionado");
            return;
        }
        message.success(
            lotes.length > 1
                ? `Se crearon ${lotes.length} tandas (${cantidadActasTotal} actas en total). Empezó a procesar la primera.`
                : `Lote #${lotes[0].loteNotificacionId} generado. Procesando ${cantidadActasTotal} actas en segundo plano...`
        );
        if (actasExcluidasPorEnCurso.length > 0) {
            message.warning(`${actasExcluidasPorEnCurso.length} acta(s) no se incluyeron porque ya estaban pendientes en otro envío en curso.`);
        }
        setEstadoLoteEnCurso(null);
        setLoteImpresionOrigenEnCurso(esTipoPorLote(tipo) ? loteImpresionSeleccionado ?? null : null);
        setLoteEnCursoId(lotes[0].loteNotificacionId);
    };

    const onConfirmarEnvio = () => {
        const descripcion = tipo === "CAMPANIA"
            ? `Se van a re-notificar ${count ?? 0} actas vencidas (campaña, ${ALCANCE_CAMPANIA_LABELS[alcanceCampania]}${excluirConveniosCampania ? ", sin las que están en convenio" : ""}). Cada una se regenera (PDF + pasarela de pago) antes de mandarse, así que puede tardar bastante si son muchas`
            : tipo === "PRODUCCION_VENCIDA"
                ? `Se van a regenerar y re-notificar ${count ?? 0} actas vencidas del lote #${loteImpresionSeleccionado?.NroLote}. Cada una se regenera con valores actualizados (PDF + nueva solicitud de pago) antes de mandarse, así que puede tardar bastante si son muchas`
                : `Se van a notificar ${count ?? 0} actas del lote #${loteImpresionSeleccionado?.NroLote}`;
        Modal.confirm({
            title: "Confirmar envío por email",
            content: `${descripcion}. Esta acción no se puede deshacer.`,
            okText: "Enviar",
            cancelText: "Cancelar",
            onOk: enviarLote,
        });
    };

    return (
        <MainLayout title="Generar lote de notificación por email">
            <LoteNotificacionDetalleModal
                loteNotificacionId={detalleLoteId}
                open={detalleLoteId != null}
                onClose={() => setDetalleLoteId(null)}
            />
            <Tabs
                items={[
                    {
                        key: "generar",
                        label: "Generar envío",
                        children: (
                            <div style={{ maxWidth: 900, margin: "0 auto" }}>
                                <CollapsibleCard title={<Space><MailOutlined /> Tipo de envío</Space>} style={{ marginBottom: 24 }}>
                                    <Radio.Group
                                        value={tipo}
                                        onChange={(e) => {
                                            setTipo(e.target.value);
                                            setLoteImpresionId(undefined);
                                            setLoteImpresionSeleccionado(undefined);
                                            setConfiguracionProduccionId(undefined);
                                            setAlcance("generadas");
                                        }}
                                    >
                                        <Radio.Button value="PRODUCCION">Producción</Radio.Button>
                                        <Radio.Button value="PRODUCCION_VENCIDA">Producción vencida</Radio.Button>
                                        <Radio.Button value="CAMPANIA">Campaña</Radio.Button>
                                    </Radio.Group>
                                    <Paragraph type="secondary" style={{ marginTop: 12, marginBottom: 0 }}>
                                        {DESCRIPCION_TIPO[tipo as keyof typeof DESCRIPCION_TIPO]}
                                    </Paragraph>
                                </CollapsibleCard>

                                <CollapsibleCard title={<Space><MailOutlined /> {esTipoPorLote(tipo) ? (tipo === "PRODUCCION_VENCIDA" ? "Elegir lote de impresión vencido" : "Elegir lote de impresión") : "Filtros de campaña"}</Space>}>
                                    {tipo === "PRODUCCION_VENCIDA" ? (
                                        <Select
                                            style={{ width: "100%" }}
                                            placeholder="Buscar por número de lote o unidad de producción"
                                            showSearch
                                            filterOption={false}
                                            loading={loadingLotesVencidos}
                                            notFoundContent={loadingLotesVencidos ? "Buscando..." : "No hay lotes con actas vencidas"}
                                            value={loteImpresionId}
                                            onSearch={buscarLotesVencidos}
                                            onChange={(value) => {
                                                setLoteImpresionId(value);
                                                const lote = lotesVencidos.find((l) => l.loteImpresionId === value);
                                                setLoteImpresionSeleccionado(lote ? aLoteImpresionOption(lote) : undefined);
                                            }}
                                        >
                                            {lotesVencidos.map((lote) => (
                                                <Select.Option key={lote.loteImpresionId} value={lote.loteImpresionId}>
                                                    Lote #{lote.nroLote} - {moment(lote.fechaHora).format("DD/MM/YYYY HH:mm")} - {lote.unidadesProdNombre ?? "-"} - {lote.cantidadVencidas} vencidas de {lote.cantidadInfracciones} actas{textoExcluidasVencidas(lote)}
                                                </Select.Option>
                                            ))}
                                        </Select>
                                    ) : tipo === "PRODUCCION" ? (
                                        <Select
                                            style={{ width: "100%" }}
                                            placeholder="Buscar por número de lote o unidad de producción"
                                            showSearch
                                            filterOption={false}
                                            loading={loadingLotesImpresion}
                                            notFoundContent={loadingLotesImpresion ? "Buscando..." : "Sin resultados"}
                                            value={loteImpresionId}
                                            onSearch={buscarLotesImpresion}
                                            onChange={(value) => {
                                                setLoteImpresionId(value);
                                                setLoteImpresionSeleccionado(lotesImpresion.find((l) => l.Id === value));
                                            }}
                                        >
                                            {lotesImpresion.map((lote) => (
                                                <Select.Option key={lote.Id} value={lote.Id}>
                                                    Lote #{lote.NroLote} - {moment(lote.FechaHora).format("DD/MM/YYYY HH:mm")} - {lote.CantidadInfracciones} actas ({lote.CantidadInfraccionesExcluidas ?? 0} excluidas)
                                                </Select.Option>
                                            ))}
                                        </Select>
                                    ) : (
                                        <Select
                                            style={{ width: "100%" }}
                                            placeholder="Seleccionar municipio"
                                            showSearch
                                            optionFilterProp="children"
                                            value={configuracionProduccionId}
                                            onChange={setConfiguracionProduccionId}
                                        >
                                            {configuracionesProduccion.map((c) => (
                                                <Select.Option key={c.Id} value={c.Id}>{nombreConfiguracionProduccion(c)}</Select.Option>
                                            ))}
                                        </Select>
                                    )}

                                    {formularioListo && esTipoPorLote(tipo) && (
                                        <>
                                            <div style={{ marginTop: 24 }}>
                                                <Text strong>Alcance</Text>
                                                <div style={{ marginTop: 8 }}>
                                                    <Radio.Group value={alcance} onChange={(e) => setAlcance(e.target.value)}>
                                                        <Radio.Button value="generadas">Solo generadas (incluidas en la impresión)</Radio.Button>
                                                        <Radio.Button value="excluidas">Solo excluidas</Radio.Button>
                                                        <Radio.Button value="ambas">Ambas</Radio.Button>
                                                    </Radio.Group>
                                                </div>
                                            </div>

                                            {/* No hay control para esto a proposito: el backend siempre exige
                                                EstadoPago = IMPAGA, EstadoJuzgamiento = NO_JUZGADA y tener email
                                                (GetFiltroInfraccionesNotificablesEmail), tanto en el preview como
                                                en el envio. Un checkbox seria mentiroso: destildarlo no cambiaria
                                                nada. Solo se aclara para que el usuario lo sepa. */}
                                            <Paragraph type="secondary" style={{ marginTop: 16, marginBottom: 0 }}>
                                                No se incluyen las actas ya pagadas ni las que tienen descargo o sentencia en curso: el sistema las descarta siempre, y el total de abajo ya las tiene descontadas.
                                            </Paragraph>
                                            {tipo === "PRODUCCION_VENCIDA" && (
                                                <Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0 }}>
                                                    Solo se toman las actas con el 2º vencimiento pasado y siempre se dejan afuera las que están en un convenio de pago. Las que quedan afuera por convenio o por no tener email se registran como descartadas en el detalle del lote, con el motivo.
                                                </Paragraph>
                                            )}

                                            <div style={{ marginTop: 24 }}>
                                                <Text strong>Excluir provincias</Text>
                                                <Select
                                                    mode="multiple"
                                                    allowClear
                                                    showSearch
                                                    style={{ width: "100%", marginTop: 8 }}
                                                    placeholder="Ninguna provincia excluida"
                                                    value={provinciasExcluir}
                                                    onChange={setProvinciasExcluir}
                                                >
                                                    {getProvinciasList().map((provincia: string) => (
                                                        <Select.Option key={provincia} value={provincia}>{provincia}</Select.Option>
                                                    ))}
                                                </Select>
                                            </div>

                                            <div style={{ marginTop: 24 }}>
                                                <Text strong>Excluir códigos postales</Text>
                                                <div style={{ marginTop: 8 }}>
                                                    <TagInput textButton="Agregar código postal" value={cpExcluir} onChange={setCpExcluir} />
                                                </div>
                                            </div>
                                        </>
                                    )}

                                    {formularioListo && tipo === "CAMPANIA" && (
                                        <>
                                            <div style={{ marginTop: 24 }}>
                                                <Text strong>Rango de fecha de infracción (opcional)</Text>
                                                <div style={{ marginTop: 8 }}>
                                                    <DatePicker.RangePicker
                                                        style={{ width: "100%" }}
                                                        format="DD/MM/YYYY"
                                                        locale={locale}
                                                        value={fechaInfraccionRange}
                                                        onChange={(range) => setFechaInfraccionRange(range as [Moment, Moment] | null)}
                                                    />
                                                </div>
                                            </div>

                                            <div style={{ marginTop: 24 }}>
                                                <Text strong>Alcance</Text>
                                                <div style={{ marginTop: 8 }}>
                                                    <Radio.Group value={alcanceCampania} onChange={(e) => setAlcanceCampania(e.target.value)}>
                                                        <Radio.Button value="generadas">Solo impresión generada</Radio.Button>
                                                        <Radio.Button value="excluidas">Solo impresión excluida</Radio.Button>
                                                        <Radio.Button value="ambas">Ambas</Radio.Button>
                                                    </Radio.Group>
                                                </div>
                                            </div>

                                            <div style={{ marginTop: 16 }}>
                                                <Checkbox checked={excluirConveniosCampania} onChange={(e) => setExcluirConveniosCampania(e.target.checked)}>
                                                    Excluir actas en convenio de pago
                                                </Checkbox>
                                            </div>

                                            <Collapse ghost style={{ marginTop: 16 }}>
                                                <Collapse.Panel header="Incluir provincia / código postal" key="incluir">
                                                    <Space direction="vertical" style={{ width: "100%" }}>
                                                        <div>
                                                            <Text>Incluir solo estas provincias</Text>
                                                            <Select
                                                                mode="multiple"
                                                                allowClear
                                                                showSearch
                                                                style={{ width: "100%", marginTop: 8 }}
                                                                placeholder="Todas las provincias"
                                                                value={provinciasIncluirCampania}
                                                                onChange={setProvinciasIncluirCampania}
                                                            >
                                                                {getProvinciasList().map((provincia: string) => (
                                                                    <Select.Option key={provincia} value={provincia}>{provincia}</Select.Option>
                                                                ))}
                                                            </Select>
                                                        </div>
                                                        <div>
                                                            <Text>Incluir códigos postales puntuales</Text>
                                                            <div style={{ marginTop: 8 }}>
                                                                <TagInput textButton="Agregar código postal" value={cpIncluirCampania} onChange={setCpIncluirCampania} />
                                                            </div>
                                                        </div>
                                                    </Space>
                                                </Collapse.Panel>
                                                <Collapse.Panel header="Excluir provincia / código postal" key="excluir">
                                                    <Space direction="vertical" style={{ width: "100%" }}>
                                                        <div>
                                                            <Text>Excluir provincias</Text>
                                                            <Select
                                                                mode="multiple"
                                                                allowClear
                                                                showSearch
                                                                style={{ width: "100%", marginTop: 8 }}
                                                                placeholder="Ninguna provincia excluida"
                                                                value={provinciasExcluirCampania}
                                                                onChange={setProvinciasExcluirCampania}
                                                            >
                                                                {getProvinciasList().map((provincia: string) => (
                                                                    <Select.Option key={provincia} value={provincia}>{provincia}</Select.Option>
                                                                ))}
                                                            </Select>
                                                        </div>
                                                        <div>
                                                            <Text>Excluir códigos postales</Text>
                                                            <div style={{ marginTop: 8 }}>
                                                                <TagInput textButton="Agregar código postal" value={cpExcluirCampania} onChange={setCpExcluirCampania} />
                                                            </div>
                                                        </div>
                                                    </Space>
                                                </Collapse.Panel>
                                            </Collapse>

                                            <Collapse ghost style={{ marginTop: 16 }}>
                                                <Collapse.Panel header="Opciones avanzadas de regeneración" key="avanzado">
                                                    <Space direction="vertical">
                                                        <div>
                                                            <Text>Días para el nuevo vencimiento</Text>
                                                            <div>
                                                                <InputNumber min={0} value={diasVencimiento} onChange={(v) => setDiasVencimiento(v ?? 0)} />
                                                                <Text type="secondary" style={{ marginLeft: 8 }}>0 = usa el default configurado para el municipio</Text>
                                                            </div>
                                                        </div>
                                                        <Checkbox checked={mantenerValoresRegeneracion} onChange={(e) => setMantenerValoresRegeneracion(e.target.checked)}>
                                                            Mantener los valores originales de UF/cotización (no recalcular)
                                                        </Checkbox>
                                                    </Space>
                                                </Collapse.Panel>
                                            </Collapse>

                                            <div style={{ marginTop: 16 }}>
                                                <Checkbox checked={soloVencidasPreview} onChange={(e) => setSoloVencidasPreview(e.target.checked)}>
                                                    Mostrar en la previsualización solo las ya vencidas (no afecta el envío)
                                                </Checkbox>
                                            </div>
                                        </>
                                    )}

                                    {formularioListo && (
                                        <>
                                            <div style={{ marginTop: 24, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                                <Statistic title="Actas a notificar" value={count ?? 0} loading={countLoading} />
                                                <Button
                                                    type="primary"
                                                    size="large"
                                                    icon={<SendOutlined />}
                                                    disabled={!count || countLoading || hayLotePropioActivo}
                                                    loading={sending}
                                                    onClick={onConfirmarEnvio}
                                                    title={hayLotePropioActivo ? "Esperá a que termine el envío en curso antes de disparar otro" : undefined}
                                                >
                                                    Enviar por email
                                                </Button>
                                            </div>
                                            {hayLotePropioActivo && (
                                                <Text type="warning">
                                                    Hay un envío en curso (lote #{loteEnCursoId}). Esperá a que termine para evitar notificaciones duplicadas.
                                                </Text>
                                            )}
                                            {!!sinEmail && (
                                                <Text type="secondary" style={{ display: "block" }}>
                                                    {sinEmail} acta(s) más matchean el filtro pero no tienen email y quedan afuera
                                                    {tipo === "PRODUCCION_VENCIDA" ? " (quedan registradas como descartadas en el detalle del lote)." : "."}
                                                </Text>
                                            )}
                                            {!!enConvenio && (
                                                <Text type="secondary" style={{ display: "block" }}>
                                                    {enConvenio} acta(s) más matchean el filtro pero están en un convenio de pago y quedan afuera (quedan registradas como descartadas en el detalle del lote).
                                                </Text>
                                            )}

                                            <div style={{ marginTop: 24 }}>
                                                <Text strong>Previsualización (primeras {preview.length} actas)</Text>
                                                <Table
                                                    style={{ marginTop: 8 }}
                                                    size="small"
                                                    rowKey="infraccionId"
                                                    dataSource={preview}
                                                    pagination={false}
                                                    locale={{ emptyText: <Empty description="Sin actas para este filtro" /> }}
                                                >
                                                    <Table.Column title="Nro. trámite" dataIndex="nroTramite" />
                                                    <Table.Column title="Titular" dataIndex="titularNombreCompleto" render={(v?: string) => v ?? "-"} />
                                                    <Table.Column title="Provincia" dataIndex="titularProvincia" render={(v?: string) => v ?? "-"} />
                                                    <Table.Column title="Cod. postal" dataIndex="titularCodigoPostal" render={(v?: string) => v ?? "-"} />
                                                    <Table.Column
                                                        title="Notificada antes"
                                                        dataIndex="actaEnviadaEmail"
                                                        render={(yaEnviada: boolean) => (
                                                            <Tag color={yaEnviada ? "blue" : "default"}>{yaEnviada ? "Sí" : "No"}</Tag>
                                                        )}
                                                    />
                                                </Table>
                                            </div>
                                        </>
                                    )}
                                </CollapsibleCard>

                                {loteEnCursoId && (
                                    <CollapsibleCard title={`Envío en proceso — Lote #${loteEnCursoId}`} style={{ marginTop: 24 }}>
                                        {loteImpresionOrigenEnCurso && (
                                            <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
                                                Generado a partir del lote de impresión #{loteImpresionOrigenEnCurso.NroLote}
                                            </Text>
                                        )}
                                        <EstadoLoteNotificacion
                                            loteNotificacionId={loteEnCursoId}
                                            onEstadoChange={setEstadoLoteEnCurso}
                                            onVerDetalle={setDetalleLoteId}
                                            compact
                                        />
                                    </CollapsibleCard>
                                )}
                            </div>
                        ),
                    },
                    {
                        key: "historial",
                        label: "Historial de lotes",
                        children: <HistorialLotesNotificacion key={historialKey} />,
                    },
                    {
                        key: "resumen",
                        label: "Resumen",
                        children: <ResumenLotesNotificacion />,
                    },
                ]}
                onChange={(key) => {
                    if (key === "historial") setHistorialKey((k) => k + 1);
                }}
            />
        </MainLayout>
    );
};
